import { useState, useEffect, useRef } from "react";

/**
 * Visibility-based scrollspy.
 *
 * For every registered section, we measure how many pixels of it are
 * currently visible inside the content window (viewport minus navbar).
 * The section with the most visible pixels wins — no direction math,
 * no threshold tricks, no scoring.
 *
 * Special cases:
 *   scrollY < 10          → null  (hero, nothing active)
 *   page bottom           → last section locked in
 *   no section visible    → null  (avoids false positives during hero)
 */
const useScrollSpy = (
  sectionIds,
  getNavbarHeight,
  offset = 20,
  updateUrl = false,
  navigate = null
) => {
  const [activeSection, setActiveSection] = useState(null);

  // Refs so the scroll listener never needs to re-register
  const sectionIdsRef      = useRef(sectionIds);
  const getNavbarHeightRef = useRef(getNavbarHeight);
  const navigateRef        = useRef(navigate);
  const updateUrlRef       = useRef(updateUrl);

  // Always current, no deps
  useEffect(() => {
    sectionIdsRef.current      = sectionIds;
    getNavbarHeightRef.current = getNavbarHeight;
    navigateRef.current        = navigate;
    updateUrlRef.current       = updateUrl;
  });

  // Avoid redundant state writes from the scroll handler
  const lastActive = useRef(null);
  const ticking    = useRef(false);

  // URL sync — only fires when activeSection state actually changes
  useEffect(() => {
    if (!updateUrlRef.current || !navigateRef.current) return;
    // Guard: if none of the tracked sections exist in the DOM we're on a page
    // that doesn't have these sections (e.g. a solution sub-page). Don't redirect.
    const anySectionExists = sectionIdsRef.current.some(
      (id) => document.getElementById(id) !== null
    );
    if (!anySectionExists) return;
    if (activeSection) {
      navigateRef.current(`/${activeSection}`, { replace: true });
    } else {
      navigateRef.current("/", { replace: true });
    }
  }, [activeSection]);

  // Single stable listener — empty deps, registered once for the hook's lifetime
  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const ids     = sectionIdsRef.current;
        const iH      = window.innerHeight;

        const navH =
          typeof getNavbarHeightRef.current === "function"
            ? getNavbarHeightRef.current()
            : (getNavbarHeightRef.current ?? 0);

        // ── At the very top: nothing active ──────────────────────────
        if (scrollY < 10) {
          if (lastActive.current !== null) {
            lastActive.current = null;
            setActiveSection(null);
          }
          ticking.current = false;
          return;
        }

        // ── At the bottom: lock to last section ──────────────────────
        if (scrollY + iH >= document.body.scrollHeight - 50) {
          const last = ids[ids.length - 1];
          if (lastActive.current !== last) {
            lastActive.current = last;
            setActiveSection(last);
          }
          ticking.current = false;
          return;
        }

        // ── Visibility contest ────────────────────────────────────────
        // Score each section by what percentage of itself is visible.
        // A short section that's 70% on-screen beats a tall section
        // that's only 15% on-screen — this matches AppCenter's approach
        // and prevents long sections from stealing the highlight from
        // short ones that are fully in view.
        // Minimum: at least 8% of the content window in raw pixels
        // must be visible to avoid false positives near the hero.
        const contentH  = iH - navH;
        const minPixels = contentH * 0.08;

        let bestId  = null;
        let bestPct = 0;
        let bestPx  = 0;

        for (const id of ids) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect   = el.getBoundingClientRect();
          const visTop = Math.max(rect.top, navH);
          const visBot = Math.min(rect.bottom, iH);
          const visPx  = Math.max(0, visBot - visTop);
          const visPct = rect.height > 0 ? visPx / rect.height : 0;

          if (visPct > bestPct) {
            bestPct = visPct;
            bestPx  = visPx;
            bestId  = id;
          }
        }

        const active = bestPx >= minPixels ? bestId : null;

        if (active !== lastActive.current) {
          lastActive.current = active;
          setActiveSection(active);
        }

        ticking.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // run once on mount

    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // ← single registration

  return activeSection;
};

export default useScrollSpy;
